// Roles
export const ORG_ROLES = ['owner', 'admin', 'manager', 'member'];
export const AREA_MEMBER_ROLES = ['member', 'admin'];
export const EXTERNAL_ROLE = 'external';
// Area types
export const AREA_TYPES = ['persistent', 'project'];
export const AREA_VISIBILITY = ['public', 'private'];
// Message view styles
export const MESSAGE_VIEW_STYLES = ['classic', 'bubble'];
// Message types
export const MESSAGE_TYPES = ['text', 'media', 'announcement', 'voice', 'system', 'poll'];
// Notification levels
export const NOTIFICATION_LEVELS = ['all', 'mentions', 'muted'];
// 2FA methods
export const TWO_FACTOR_METHODS = ['totp', 'sms'];
// Magic link purposes
export const MAGIC_LINK_PURPOSES = ['login', 'invite', 'email_verify', 'external_invite'];
// Media processing status
export const PROCESSING_STATUSES = ['pending', 'processing', 'completed', 'failed'];
// Link preview fetch status
export const FETCH_STATUSES = ['pending', 'success', 'failed'];
// Push token platforms
export const PUSH_PLATFORMS = ['ios', 'android', 'web'];
// Invite roles (can't invite owners)
export const INVITE_ROLES = ['admin', 'manager', 'member'];
// Default reactions
export const DEFAULT_REACTIONS = ['👍', '❤️', '😂', '😮', '🎉', '✅'];
// Limits
export const MAX_FILE_SIZE_BYTES = 2_147_483_648; // 2GB — hard ceiling (large videos use multipart)
export const MAX_IMAGE_UPLOAD_BYTES = 26_214_400; // 25MB — friendly client-side cap for images (pre-downscale)
export const MAX_VOICE_MEMO_SIZE_BYTES = 104_857_600; // 100MB — voice memos are short clips, keep a tight cap
export const MESSAGE_EDIT_WINDOW_MS = 300_000; // 5 minutes
// Upload reliability tuning
export const MULTIPART_THRESHOLD_BYTES = 16_777_216; // 16MB — files larger than this use S3 multipart upload
export const MULTIPART_PART_SIZE_BYTES = 8_388_608; // 8MB per part (S3 minimum part size is 5MB, except the last)
export const MULTIPART_MAX_PARTS = 10_000; // S3 hard limit on parts per upload
export const MULTIPART_MAX_CONCURRENT_PARTS = 4; // parts uploaded in parallel
export const UPLOAD_MAX_CONCURRENT_FILES = 3; // files uploaded in parallel
export const UPLOAD_MAX_RETRIES = 3; // retry attempts per PUT (single or part) before failing
export const IMAGE_MAX_UPLOAD_DIMENSION = 2048; // client-side downscale target (longest edge, px)
export const READ_RECEIPT_RETENTION_DAYS = 90;
export const DEFAULT_ACK_REMINDER_HOURS = 24;
export const ACK_REMINDER_CHECK_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes
export const READ_RECEIPT_PRUNE_INTERVAL_MS = 24 * 60 * 60 * 1000; // daily
export const DEFAULT_VOICE_MEMO_RETENTION_DAYS = 90;
export const MAX_DM_PARTICIPANTS = 10;
export const MAX_VOICE_MEMO_DURATION_SECONDS = 300; // 5 minutes
export const VOICE_MEMO_WAVEFORM_SAMPLES = 64;
/**
 * How long an unfinished voice memo may sit before it is swept.
 *
 * `POST /voice-memos/presign` inserts the row immediately, but `expiresAt` is
 * only set on `/complete`. So a cancelled or failed recording leaves a row with
 * a NULL `expiresAt` — and the cleanup job matched `expiresAt < now()`, which
 * NULL never satisfies. Those rows, and any S3 object already uploaded against
 * them, were immortal: a dev database accumulated 204 of them over five months.
 *
 * Generous on purpose. This is a live upload window, not a retention policy —
 * sweeping a memo out from under a slow upload would lose a real recording.
 */
export const ABANDONED_VOICE_MEMO_HOURS = 24;
export const JWT_ACCESS_EXPIRY = '15m';
export const JWT_REFRESH_EXPIRY_DAYS = 30;
export const MAGIC_LINK_EXPIRY_MINUTES = 15;
// Reply Later reminders
export const REPLY_LATER_REMINDER_INTERVALS = [24, 72, 168]; // hours
export const REPLY_LATER_REMINDER_CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6 hours
export const INBOX_MAX_UNREAD_SCAN = 200;
/**
 * How far back an unacknowledged announcement still counts as something you
 * owe. Announcements created through the composer live on a thread message,
 * and the ack queries used to exclude every threaded message — so they never
 * reached anyone's inbox at all. Fixing that makes historic announcements
 * visible all at once, and without a floor the first open after the fix is a
 * wall of months-old items. Also bounds the scan.
 */
export const ACK_PENDING_WINDOW_DAYS = 30;
// ============================================================
// Todos
// ============================================================
/**
 * The four things that can appear in Todos. Three are views over data that
 * already exists — an announcement you owe an acknowledgement on, a message you
 * flagged for reply later, an open poll you have not answered — and only `task`
 * is a persisted row of its own.
 *
 * `GET /todos?type=` is MULTI-select (comma-separated), unlike the inbox's
 * single-valued enum, so a client can ask for "everything except unread noise".
 */
export const TODO_TYPES = ['ack', 'reply_later', 'poll', 'task'];
/** `GET /todos?status=` — which side of the open/done line to list. */
export const TODO_STATUS_FILTERS = ['open', 'done', 'all'];
/**
 * Per-item state, returned on every row.
 *
 * Deliberately server-derived: reply-later's mistake is that "active" exists
 * only as a query param, never in a response body, so every client recomputes
 * `snoozedUntil > now()` for itself (web inbox and both mobile inboxes each do
 * it separately today).
 */
export const TODO_ITEM_STATUSES = ['open', 'snoozed', 'done'];
export const TODOS_DEFAULT_PAGE_SIZE = 25;
export const TODOS_MAX_PAGE_SIZE = 50;
export const MAX_TASK_TITLE_LENGTH = 500;
export const MAX_TASK_NOTES_LENGTH = 5000;
// Link previews
export const LINK_PREVIEW_FETCH_TIMEOUT_MS = 5000;
export const MAX_LINK_PREVIEWS_PER_MESSAGE = 3;
export const URL_REGEX = /https?:\/\/[^\s<>"{}|\\^`[\]]+/gi;
// DND & Status
export const DND_DURATION_PRESETS = [1, 4, 8, 24]; // hours
export const STATUS_PRESETS = [
    { emoji: '\u{1F4C5}', text: 'In a meeting' },
    { emoji: '\u{1F389}', text: 'At an event' },
    { emoji: '\u{1F3D6}\u{FE0F}', text: 'On PTO' },
    { emoji: '\u{1F3AF}', text: 'Focused' },
];
export const SCHEDULED_MESSAGE_DISPATCH_INTERVAL_MS = 60_000; // 1 min
export const STATUS_EXPIRY_CHECK_INTERVAL_MS = 300_000; // 5 min
export const DEFAULT_WORKING_HOURS = {
    monday: { start: '09:00', end: '17:00' },
    tuesday: { start: '09:00', end: '17:00' },
    wednesday: { start: '09:00', end: '17:00' },
    thursday: { start: '09:00', end: '17:00' },
    friday: { start: '09:00', end: '17:00' },
};
// Account lockout
export const MAX_LOGIN_ATTEMPTS = 5;
export const ACCOUNT_LOCKOUT_DURATION_SECONDS = 900; // 15 minutes
// 2FA & Security
export const RECOVERY_CODE_COUNT = 10;
export const TWO_FACTOR_TOKEN_EXPIRY = '5m';
export const SESSION_CLEANUP_INTERVAL_MS = 86_400_000; // daily
export const SECURITY_AUDIT_ACTIONS = [
    '2fa.enabled',
    '2fa.disabled',
    '2fa.verified',
    '2fa.recovery_used',
    'session.revoked',
    'session.revoked_all',
    'session.reuse_detected',
    'login.success',
    'login.failed',
    'login.2fa_required',
    'account.locked',
];
// Notification queue
export const NOTIFICATION_QUEUE_CHECK_INTERVAL_MS = 60_000; // 1 minute
// Email digest
export const DIGEST_CHECK_INTERVAL_MS = 60 * 60 * 1000; // 1 hour
export const DEFAULT_DIGEST_HOUR = 8; // 8am local time
// DND digest (catch-up email when DND expires)
export const DND_DIGEST_CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
// External access
export const EXTERNAL_MAGIC_LINK_EXPIRY_HOURS = 72;
export const MAGIC_LINK_CLEANUP_INTERVAL_MS = 3_600_000; // 1 hour
// Mention autocomplete
export const MENTION_TRIGGER = '@';
export const MENTION_SEARCH_DEBOUNCE_MS = 200;
export const MENTION_AUTOCOMPLETE_LIMIT = 8;
// Polls
export const MAX_POLL_OPTIONS = 10;
export const MIN_POLL_OPTIONS = 2;
export const MAX_POLL_QUESTION_LENGTH = 500;
export const MAX_POLL_OPTION_LENGTH = 200;
// Custom emoji
export const CUSTOM_EMOJI_NAME_REGEX = /^[a-z0-9_]{2,32}$/;
export const CUSTOM_EMOJI_MAX_SIZE_BYTES = 1_048_576; // 1MB
export const CUSTOM_EMOJI_DIMENSION = 64; // resize to 64x64
// Slash commands (client-side shortcuts)
export const SLASH_COMMANDS = [
    { command: 'poll', description: 'Create a poll', icon: 'bar-chart' },
    { command: 'status', description: 'Set your status', icon: 'smile' },
    { command: 'dnd', description: 'Do Not Disturb', icon: 'moon' },
    { command: 'schedule', description: 'Schedule a message', icon: 'clock' },
    { command: 'thread', description: 'Start a new thread', icon: 'message-square' },
];
// Pagination
export const DEFAULT_PAGE_SIZE = 50;
export const MAX_PAGE_SIZE = 100;
// Allowed file types
// Drafts
export const DRAFT_TYPES = ['message', 'thread', 'announcement'];
export const DRAFT_CLOUD_SAVE_DEBOUNCE_MS = 2000;
export const DRAFT_MESSAGE_SAVE_DEBOUNCE_MS = 3000;
export const ALLOWED_IMAGE_TYPES = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/heic',
];
export const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];
export const ALLOWED_AUDIO_TYPES = [
    'audio/mp4',
    'audio/mpeg',
    'audio/ogg',
    'audio/wav',
    'audio/webm',
];
export const ALLOWED_FILE_TYPES = [
    ...ALLOWED_IMAGE_TYPES,
    ...ALLOWED_VIDEO_TYPES,
    ...ALLOWED_AUDIO_TYPES,
];
/**
 * Emoji a visitor may leave on a public changelog entry.
 *
 * A fixed allowlist, not free text: the endpoint is unauthenticated, so
 * anything accepted here is arbitrary user content rendered on a marketing
 * page. Six is enough to feel expressive without turning into a keyboard.
 */
export const UPDATE_REACTION_EMOJI = ['🎉', '👏', '🙌', '❤️', '🚀', '👀'];
//# sourceMappingURL=index.js.map